import { Shield, CheckCircle2, AlertTriangle, ExternalLink } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Link } from "react-router-dom";

const CODE_SAFEGUARDS = [
  ["Unique user IDs (§164.312(a)(2)(i))", "Each therapist has a UUID; PHI is scoped to their team_id"],
  ["Automatic logoff (§164.312(a)(2)(iii))", "Sessions end after 15 minutes of inactivity with a 1-minute warning"],
  ["Encryption in transit (§164.312(e)(1))", "TLS 1.2+ enforced on all API calls (managed by hosting)"],
  ["Encryption at rest", "MongoDB Atlas WiredTiger encryption at rest (must be enabled by admin)"],
  ["Password strength (§164.308(a)(5)(ii)(D))", "Min 10 characters, mixed case + digit"],
  ["Failed login lockout (§164.308(a)(5)(ii)(C))", "5 failed attempts in 15 min → 15-minute lock"],
  ["Audit log (§164.312(b))", "Append-only log of learner views, deletes, exports, logins, role changes"],
  ["Access controls / RBAC (§164.312(a)(1))", "Owner / Editor / Viewer roles; only owners delete learners"],
  ["Session integrity", "HttpOnly + Secure cookies; JWT signed with 256-bit secret"],
  ["Right to access (§164.524)", "Per-learner PDF and CSV export available on demand"],
  ["Right to deletion (§164.526)", "Owner can delete any learner and cascading assignments"],
];

const OWNER_MUST = [
  ["Sign a Business Associate Agreement (BAA)", "MongoDB Atlas offers one on the Enterprise tier. Required by §164.308(b)."],
  ["Sign a BAA with your hosting provider", "Emergent, Vercel, Railway, AWS — verify each vendor that stores or processes PHI."],
  ["Do NOT send learner names or identifiers to Gemini AI photo generation", "Only card labels (e.g., 'apple') are sent — never a learner's name. This is enforced in code, but audit before enabling in production."],
  ["Complete a Risk Analysis (§164.308(a)(1)(ii)(A))", "Document threats to PHI and mitigations."],
  ["Written HIPAA policies and workforce training (§164.530)", "Include breach notification, sanction, and data backup policies."],
  ["Physical safeguards for any device that accesses the app (§164.310)", "Screen locks, encrypted devices, secure disposal."],
  ["Register as a Covered Entity or Business Associate as appropriate", "See hhs.gov/hipaa."],
  ["Configure encrypted, off-site backups with retention", "MongoDB Atlas continuous backup is one option."],
];

export default function CompliancePage() {
  return (
    <div className="p-4 sm:p-8 md:p-12 max-w-4xl">
      <PageHeader
        eyebrow="Security & Privacy"
        title="HIPAA Compliance"
        description="Discrimina ships with the technical safeguards required by the HIPAA Security Rule. Full HIPAA compliance also depends on administrative and legal steps you complete outside the app."
      />

      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-8 flex gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <div className="font-display font-bold text-amber-900">Not compliant until you complete the checklist below</div>
          <div className="text-sm text-amber-800 mt-1">
            Technical safeguards alone are ~30% of HIPAA. You must also complete BAAs, policies, and training.
          </div>
        </div>
      </div>

      <section className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-5 h-5 text-emerald-600" />
          <h2 className="font-display font-bold text-xl">Built in — technical safeguards</h2>
        </div>
        <ul className="space-y-3">
          {CODE_SAFEGUARDS.map(([title, desc]) => (
            <li key={title} className="flex gap-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-1" />
              <div>
                <div className="font-semibold text-sm text-slate-900">{title}</div>
                <div className="text-sm text-slate-600">{desc}</div>
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="bg-white border border-slate-200 rounded-2xl p-6 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="w-5 h-5 text-rose-600" />
          <h2 className="font-display font-bold text-xl">Your responsibility — administrative & legal</h2>
        </div>
        <ul className="space-y-3">
          {OWNER_MUST.map(([title, desc]) => (
            <li key={title} className="flex gap-3">
              <span className="w-4 h-4 rounded-full border-2 border-slate-300 shrink-0 mt-1" />
              <div>
                <div className="font-semibold text-sm text-slate-900">{title}</div>
                <div className="text-sm text-slate-600">{desc}</div>
              </div>
            </li>
          ))}
        </ul>
        <a
          href="https://www.hhs.gov/hipaa/for-professionals/security/index.html"
          target="_blank" rel="noreferrer"
          className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-800"
        >
          HHS HIPAA Security Rule <ExternalLink className="w-3.5 h-3.5" />
        </a>
      </section>

      <Link
        to="/audit"
        data-testid="view-audit-link"
        className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900 hover:text-blue-600"
      >
        View audit log →
      </Link>
    </div>
  );
}
